import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "preact/jsx-runtime";
import { useFormContext, } from 'react-hook-form';
import { useMemo, useCallback, useContext, useEffect, } from 'preact/compat';
import useDidMount from 'beautiful-react-hooks/useDidMount';
import { ContinueButton } from '@bookingForm/styles/buttons';
import LoadingSpinner from '@widgets/shared/components/LoadingSpinner';
import { DummyLink } from '@genericBookings/components/SelectedOption/styles';
import NumberInput from '@widgets/shared/components/NumberInput';
import useGetFormDataAsParams from '@genericBookings/hooks/useGetFormDataAsParams';
import { useAppDispatch, useAppSelector } from '@genericBookings/store/hooks';
import { getActivities, getFetchingStatus, readSessions, setIsSelectedForActivity, setVisitorsForActivity, } from '@genericBookings/store/reducers/activities';
import { useStepCompletion } from '@widgets/DayTickets/context/Steps';
import { numberArrayToVisitors, visitorCount, visitorsFromSchema } from '@genericBookings/visitorUtils';
import { defaultVisitors } from '@genericBookings/types/Visitors';
import { useTimeConflict } from '@bookingForm/context/TimeConflicts';
import useQueryParams from '@widgets/DIYDayTickets/hooks/useQueryParams';
import Band from './components/Band/Band';
import { BandsList } from './components/Band/styles';
import InfoBand from './components/InfoBand';
import { Actions, Element } from './styles';
function NewAttendeesSelector(props) {
    const { name: fieldName, onNextSection, canProceed, prefixName, canProceedText, showButtonWhenDisabled, loggedIn, options, accent, productId, activity, } = props;
    const getSuccessUrlData = useGetFormDataAsParams();
    const { modifyStep, activitiesStep, attendeesStep } = useStepCompletion();
    const { initTimePeriods, } = useTimeConflict();
    const themeOptions = useContext(options);
    const { analyticsTitle, analyticsUrl, analyticsPath, showPriceOnBands, isBuilder, isMulti, visitorInfoBands = [], } = themeOptions;
    const step = isBuilder ? activitiesStep : attendeesStep;
    const activities = useAppSelector(getActivities);
    const dispatch = useAppDispatch();
    const { getValues, setValue } = useFormContext();
    const values = getValues();
    const visitorArray = getValues(Array(9).fill(0).map((_, idx) => `${prefixName}Visitors${idx}`));
    const date = getValues('VisitDate');
    const { requestingProductBands, requestingPriceBands } = useAppSelector(getFetchingStatus);
    const isLoading = useMemo(() => requestingProductBands || requestingPriceBands, [requestingProductBands, requestingPriceBands]);
    const { visitors: paramVisitors, } = useQueryParams(options);
    const handleDidMount = useCallback(async () => {
        modifyStep(step, step.isComplete, true);
        if (!isBuilder && paramVisitors && visitorCount(paramVisitors) > 0) {
            Object.keys(paramVisitors).forEach((key, idx) => {
                setValue(`Visitors${idx}`, paramVisitors[key]);
            });
            // TODO: something with visitors params here...
            // await dispatch(readSessions({
            //   activities,
            //   date,
            //   productId,
            //   values,
            //   isMulti,
            // }));
        }
    }, [
        isBuilder,
        modifyStep,
        paramVisitors,
        setValue,
        step,
    ]);
    const getActivityIdx = useCallback((currActivity) => activities.findIndex((loopActivity) => currActivity?.activity.id === loopActivity.activity.id), [activities]);
    const getVisitors = useCallback(() => {
        if (!isBuilder) {
            return numberArrayToVisitors(visitorArray);
        }
        if (activity) {
            const activityIdx = getActivityIdx(activity);
            if (activityIdx) {
                return visitorsFromSchema(getValues(`Sessions.${activityIdx}`));
            }
        }
        return defaultVisitors;
    }, [activity, getActivityIdx, getValues, isBuilder, visitorArray]);
    useEffect(() => {
        const visitors = getVisitors();
        const visitorsCount = visitorCount(visitors);
        // if an activity is passed just set the visitors for this activity
        if (activity) {
            if (activity && activity.visitors && visitorsCount !== visitorCount(activity.visitors)) {
                dispatch(setVisitorsForActivity({
                    currActivity: activity,
                    visitors,
                }));
            }
            else {
                console.error('Activity: ', `${activity} Not found in state`);
            }
        }
        else {
            const changedActivities = activities.filter((changed) => changed.visitors && visitorCount(changed.visitors) !== visitorsCount);
            changedActivities.forEach((active) => dispatch(setVisitorsForActivity({
                currActivity: active,
                visitors,
            })));
        }
    }, [activities, activity, dispatch, getVisitors, visitorArray]);
    const prices = useMemo(() => {
        // is generic day ticket so don't show the price bands...
        if (isMulti && !isBuilder) {
            return [];
        }
        if (activity) {
            return activity.activity.priceBands ?? [];
        }
        return [];
    }, [activity, isBuilder, isMulti]);
    const bands = useMemo(() => {
        if (isMulti && !isBuilder) {
            // I know it's confusing but priceBands here is actually Product bands, I can't change the name
            const { priceBands: productBands } = themeOptions;
            return productBands;
        }
        if (activity) {
            return activity.activity.productBands ?? [];
        }
        return [];
    }, [activity, isBuilder, isMulti, themeOptions]);
    useDidMount(handleDidMount);
    const nextSection = useCallback(async () => {
        if (typeof gtag !== 'undefined') {
            gtag('event', 'page_view', {
                page_title: `${analyticsTitle} - Group size`,
                page_location: `${analyticsUrl}step-2`,
                page_path: `${analyticsPath}step-2`,
                send_to: 'UA-44012490-1',
            });
        }
        // generic options. If builder then it's wrapped in a modal and we have to handle differently. Unfortunately.
        if (!isBuilder) {
            await dispatch(readSessions({
                activities,
                date,
                productId,
                values,
                isMulti,
            }));
            // console.log({
            //   activities,
            //   date,
            //   productId,
            //   values,
            //   isMulti,
            // });
            // making sure all activities have visitors
            modifyStep(step, true, true, true);
            return;
        }
        if (isBuilder && activity) {
            dispatch(setIsSelectedForActivity(true, activity));
        }
        if (isMulti && !isBuilder) {
            initTimePeriods([]);
        }
        if (onNextSection) {
            onNextSection();
        }
        // making sure all activities have visitors
        if (!isBuilder) {
            modifyStep(step, true, true, true);
        }
    }, [
        activities,
        activity,
        analyticsPath,
        analyticsTitle,
        analyticsUrl,
        date,
        dispatch,
        initTimePeriods,
        isBuilder,
        isMulti,
        modifyStep,
        onNextSection,
        productId,
        step,
        values,
    ]);
    const genericBands = useMemo(() => bands.map((band, idx) => ({
        ...band,
        FieldName: `${prefixName ?? ''}Visitors${idx}`,
        UnitPrice: prices.find((price) => price.PriceBandId === band.Id)?.Price ?? 0,
    })), [prefixName, prices, bands]);
    return (_jsxs(Element, { loggedIn: loggedIn, id: `control-${fieldName}`, children: [_jsx(BandsList, { children: isLoading ? (_jsx(LoadingSpinner, { colourOverride: accent })) : (_jsx(_Fragment, { children: bands && bands.length > 0 && (genericBands.map((band, index) => (_jsx(Band, { band: band, children: _jsx(NumberInput, { name: `${prefixName}Visitors${band.index ?? index}`, band: band, bands: genericBands, price: band.UnitPrice, showPrice: showPriceOnBands ?? true, accent: "#00AEEF" }) })))) })) }), !isLoading && visitorInfoBands.map((infoBand) => _jsx(InfoBand, { band: infoBand }, infoBand.description)), !loggedIn && (_jsx(DummyLink, { onClick: () => {
                    window.dispatchEvent(new CustomEvent('login.open'));
                    window.dispatchEvent(new CustomEvent('login.successUrl', { detail: getSuccessUrlData() }));
                }, children: "Member? Login for cheaper prices!" })), showButtonWhenDisabled && (_jsx(Actions, { children: _jsx(ContinueButton, { className: "btn btn--full btn--primary", type: "button", onClick: nextSection, disabled: !canProceed, children: canProceedText ?? 'Select a time' }) })), (canProceed && !showButtonWhenDisabled) && (_jsx(Actions, { children: _jsx(ContinueButton, { className: "btn btn--secondary", type: "button", onClick: nextSection, children: canProceedText ?? 'Select a time' }) }))] }));
}
NewAttendeesSelector.defaults = {
    canProceedText: 'Select a time',
    showButtonWhenDisabled: false,
    onNextSection: undefined,
    activity: undefined,
};
export default NewAttendeesSelector;
